import "./SearchFilter.scss";

import React, {
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";
import clsx from "clsx";
import * as fuzzy from "fuzzy";

import Field from "../../form/Field/Field";
import {
	sendEvent,
	TrackingContext,
	TrackingContextProps,
} from "../../../lib/util/AnalyticsUtils";
import { useMountEffect, usePrevious } from "../../../lib/hook/MiscHooks";
import { TextElement } from "../../../lib/util/LocaleUtils";

export interface SearchFilterProps {
	open?: boolean;
	empty?: boolean;
	placeholder?: TextElement;
	advanced?: boolean;
	searchProps?: Array<string | string[]>;
	items?: any[];
	onFilter?: (
		filteredItems: string[],
		value: string | undefined,
		index?: number,
	) => void;
	onChange?: (value: string | undefined) => void;
	options?: any[];
	trackingContext?: string | Partial<TrackingContextProps>;
	hasAdvanced?: boolean;
	primaryKey?: string;
}

const SearchFilter = (props: SearchFilterProps) => {
	const ref = useRef<HTMLDivElement | null>(null);
	const [searchValue, setSearchValue] = useState<string | undefined>();
	const [previousSearchValue] = usePrevious(searchValue);

	const trackingContext = useContext(TrackingContext);

	const {
		open,
		onChange,
		empty,
		advanced,
		placeholder,
		items,
		searchProps,
		hasAdvanced,
		onFilter,
		primaryKey,
		options,
	} = props;

	useMountEffect(() => {
		if (open) {
			window.setTimeout(() => {
				if (ref.current) {
					ref.current.focus();
				}
			}, 100);
		}

		sendEvent({
			eventCategory: "Search Filter",
			eventAction: "load",
			eventLabel: (trackingContext as any) || "",
		});
	});

	const reset = useCallback(() => {
		setSearchValue(undefined);
	}, []);

	useEffect(() => {
		if (open) {
			window.setTimeout(() => {
				if (ref.current) {
					ref.current.focus();
				}
			}, 100);
			reset();
		}
		// eslint-disable-next-line
	}, [open]);

	useEffect(() => {
		if (searchValue && empty) {
			reset();
		}
		// eslint-disable-next-line
	}, [empty]);

	useEffect(() => {
		if (searchValue !== previousSearchValue) {
			if (!previousSearchValue) {
				sendEvent({
					eventCategory: "Search Filter",
					eventAction: "search",
					eventLabel: (trackingContext as any) || "",
					mixpanelProperties: {
						name: "Search Filter",
					},
				});
			}

			if (onChange) {
				onChange(searchValue);
			}

			if (items) {
				if (!searchValue) {
					if (onFilter) {
						const itemNames = items.map(
							(item) => item[primaryKey || "displayName"],
						);
						onFilter(itemNames, searchValue);
					}
					return;
				}

				const arrayToSearch = (searchProps || ["displayName", "label"]).reduce(
					(total: string[], prop: string | string[]) => {
						items.forEach((item: any) => {
							if (Array.isArray(prop)) {
								const multiPropResult = prop.reduce(
									(multiTotal, propItem) =>
										`${multiTotal} ${item[propItem] || ""}`.trim(),
									"",
								);

								if (multiPropResult) {
									total.push(multiPropResult);
								}
							} else if (typeof item[prop] === "string") {
								total.push(item[prop]);
							}
						});

						return total;
					},
					[],
				);

				const filteredItems = fuzzy
					.filter(searchValue, arrayToSearch)
					.map((match) => {
						return match.original;
					});

				if (onFilter) {
					onFilter(filteredItems, searchValue);
				}
			}
		}
	}, [
		items,
		onFilter,
		previousSearchValue,
		searchProps,
		searchValue,
		trackingContext,
		primaryKey,
		onChange,
	]);

	const handleChange = useCallback(({ value }: any) => {
		setSearchValue(value);
	}, []);

	return (
		<Field
			tabIndex={"0"}
			className={clsx("SearchFilter", { "-with-advanced": hasAdvanced })}
			value={searchValue}
			filterInputOptions={options}
			showErrors={false}
			placeholder={placeholder}
			type={advanced ? "filter-box" : "text"}
			onChange={handleChange}
		/>
	);
};

export default SearchFilter;
